//
// Copyright 2025 DXOS.org
//

import { useAtomValue } from '@effect-atom/atom-react';
import React, {
  type FC,
  Fragment,
  type NamedExoticComponent,
  Profiler,
  type RefAttributes,
  Suspense,
  forwardRef,
  memo,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { log } from '@dxos/log';
import { ErrorBoundary } from '@dxos/react-error-boundary';
import { useDefaultValue } from '@dxos/react-hooks';
import { Position } from '@dxos/util';

import { Capabilities } from '../../../common';
import { type CapabilityManager } from '../../../core';
import { usePluginManager } from '../PluginManager';
import { SurfaceContext } from './context';
import { SurfaceInfo } from './SurfaceInfo';
import { useSurfaceProfilerCallback } from './SurfaceProfilerContext';
import {
  type Definition,
  type Props,
  type RoleToken,
  type TokenData,
  type TypedProps,
  type WebComponentDefinition,
} from './types';

const DEBUG = import.meta.env?.VITE_DEBUG;

const DEFAULT_PLACEHOLDER = <Fragment />;

/**
 * Wrapper component for rendering Web Component surfaces.
 * Handles creation, prop setting, and cleanup of Web Components.
 */
type WebComponentWrapperProps = {
  id?: string;
  role: string;
  data?: Record<string, any>;
  limit?: number;
  definition: WebComponentDefinition;
  [key: string]: any;
};

const WebComponentWrapper = memo(
  forwardRef<HTMLElement, WebComponentWrapperProps>(({ id, role, data, limit, definition, ...rest }, forwardedRef) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const elementRef = useRef<HTMLElement | null>(null);
    const propsRef = useRef({ id, role, data, limit, ...rest });

    // Update props ref on every render
    propsRef.current = { id, role, data, limit, ...rest };

    // Create element only once
    useEffect(() => {
      if (!containerRef.current || elementRef.current) {
        return;
      }

      // Create the Web Component
      const element = document.createElement(definition.tagName);
      elementRef.current = element;

      // Set initial properties on the Web Component
      Object.assign(element, propsRef.current);

      // Append to container
      containerRef.current.appendChild(element);

      // Setup ref forwarding if provided
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }

      // Cleanup on unmount to prevent memory leaks
      return () => {
        if (elementRef.current && containerRef.current?.contains(elementRef.current)) {
          containerRef.current.removeChild(elementRef.current);
        }
        if (typeof forwardedRef === 'function') {
          forwardedRef(null);
        } else if (forwardedRef) {
          forwardedRef.current = null;
        }
        elementRef.current = null;
      };
    }, [definition.tagName, forwardedRef]);

    // Update props on existing element without recreating it
    // This runs on every render to ensure all props (including those in `rest`) are kept up to date
    useEffect(() => {
      const element = elementRef.current;
      if (!element) {
        return;
      }

      // Update properties on the existing Web Component
      Object.assign(element, propsRef.current);
    });

    return <div ref={containerRef} />;
  }),
);

WebComponentWrapper.displayName = 'WebComponentWrapper';

type SurfaceContextProviderProps = {
  id: string;
  role: string;
  data?: Record<string, any>;
  limit?: number;
  fallback?: FC<{ error: Error; data?: any }>;
  definition: Definition;
  [key: string]: any;
};

/**
 * Wrapper component that provides context for a surface.
 */
// TODO(burdon): Allow DebugPlugin to provide different fallback using react-ui ErrorFallback.
const SurfaceContextProvider = memo(
  forwardRef<HTMLElement, SurfaceContextProviderProps>(
    ({ id, role, data, limit, fallback = ErrorFallback, definition, ...rest }, forwardedRef) => {
      const contextValue = useMemo(() => ({ id, role, data }), [id, role, data]);
      const onProfilerRender = useSurfaceProfilerCallback();
      const profilerId = `surface/${id}/${role}`;

      // Handle Web Component surfaces
      if (definition.kind === 'web-component') {
        return (
          <ErrorBoundary name='surface' resetKeys={[data]} FallbackComponent={fallback}>
            <SurfaceContext.Provider value={contextValue}>
              <WebComponentWrapper
                id={id}
                role={role}
                data={data}
                limit={limit}
                definition={definition}
                ref={forwardedRef}
                {...rest}
              />
            </SurfaceContext.Provider>
          </ErrorBoundary>
        );
      }

      // Handle React component surfaces
      const Component = definition.component;

      // TODO(burdon): Remove from production build?
      const debug = DEBUG || '__DX_DEBUG__' in window;
      if (debug) {
        return (
          <ErrorBoundary name='surface' resetKeys={[data]} FallbackComponent={fallback}>
            <div className='contents' data-id={id} data-role={role}>
              <SurfaceContext.Provider value={contextValue}>
                <SurfaceInfo ref={forwardedRef}>
                  {onProfilerRender && !profilerId.includes('org.dxos.plugin.debug') ? (
                    <Profiler id={profilerId} onRender={onProfilerRender}>
                      <Component id={id} role={role} data={data} limit={limit} {...rest} />
                    </Profiler>
                  ) : (
                    <Component id={id} role={role} data={data} limit={limit} {...rest} />
                  )}
                </SurfaceInfo>
              </SurfaceContext.Provider>
            </div>
          </ErrorBoundary>
        );
      }

      return (
        <ErrorBoundary name='surface' resetKeys={[data]} FallbackComponent={fallback}>
          <div className='contents' data-id={id} data-role={role}>
            <SurfaceContext.Provider value={contextValue}>
              <Component id={id} role={role} data={data} limit={limit} {...rest} ref={forwardedRef} />
            </SurfaceContext.Provider>
          </div>
        </ErrorBoundary>
      );
    },
  ),
);

SurfaceContextProvider.displayName = 'SurfaceContextProvider';

/**
 * A surface is a named region of the screen that can be populated by plugins.
 * The `type` prop is a {@link RoleToken} that defines which region and its
 * associated data contract.
 */
// TODO(burdon): Remove `ref` since relying on this would be error prone.
export const SurfaceComponent = memo(
  forwardRef<HTMLElement, TypedProps<RoleToken<any>>>(
    ({ id: _id, type, data: dataProp, limit, placeholder = DEFAULT_PLACEHOLDER, ...rest }, forwardedRef) => {
      const data = useDefaultValue(dataProp, () => ({}));
      // TODO(wittjosiah): This will make all surfaces depend on a single signal.
      //   This isn't ideal because it means that any change to the data will cause all surfaces to re-render.
      //   This effectively means that plugin modules which contribute surfaces need to all be activated at startup.
      //   This should be fine for now because it's how it worked prior to capabilities api anyway.
      //   In the future, it would be nice to be able to bucket the surface contributions by role.
      const surfaces = useSurfaces();

      const effectiveRole = type?.role;
      if (effectiveRole == null) {
        if (DEBUG) {
          log.warn('Surface is missing required `type` prop', { id: _id });
        }
        return null;
      }

      // NOTE: Memoizing the candidates makes the surface not re-render based on reactivity within data.
      const definitions = findCandidates(surfaces, { role: effectiveRole, data });
      const candidates = limit ? definitions.slice(0, limit) : definitions;
      if (DEBUG && candidates.length === 0) {
        log.warn('no candidates for surface', { role: effectiveRole, data });
        return null;
      }

      return (
        <Suspense fallback={placeholder}>
          {candidates.map((definition) => (
            <SurfaceContextProvider
              key={definition.id}
              id={definition.id}
              role={effectiveRole}
              data={data}
              limit={limit}
              definition={definition}
              ref={forwardedRef}
              {...rest}
            />
          ))}
        </Suspense>
      );
    },
  ),
) as (<TToken extends RoleToken<any>>(props: TypedProps<TToken> & RefAttributes<HTMLElement>) => React.ReactNode) &
  NamedExoticComponent<TypedProps<RoleToken<any>> & RefAttributes<HTMLElement>>;

SurfaceComponent.displayName = 'Surface';

// TODO(burdon): Make user facing, with telemetry.
const ErrorFallback = ({ error }: { error: Error }) => {
  const { message } = error instanceof Error ? error : { message: String(error) };
  return (
    <div role='alert' data-testid='error-boundary-fallback'>
      <h1 className='flex gap-2 text-sm mt-2 text-info-text'>{message}</h1>
    </div>
  );
};

const findCandidates = (surfaces: Definition[], { role, data }: { role: string; data: Props['data'] }) => {
  return Object.values(surfaces)
    .filter((definition) =>
      Array.isArray(definition.role) ? definition.role.includes(role) : definition.role === role,
    )
    .filter(({ filter }) => (filter ? filter(data ?? {}, role) : true))
    .toSorted(Position.compare);
};

/**
 * @internal
 */
export const useSurfaces = () => {
  const manager = usePluginManager();
  const surfacesByModule = useAtomValue(manager.capabilities.atomByModule(Capabilities.ReactSurface));
  return useMemo(() => {
    const result: Definition[] = [];
    for (const [_moduleId, surfaces] of Object.entries(surfacesByModule)) {
      for (const def of surfaces.flat()) {
        result.push(def);
      }
    }
    return result;
  }, [surfacesByModule]);
};

/**
 * @returns `true` if there is a contributed surface which matches the specified role & data, `false` otherwise.
 *
 * Typed: pass a `type` role token and `data` is constrained to the token's
 * declared contract (e.g. `AppSurface.Section` requires `attendableId`).
 */
export function isSurfaceAvailable<TToken extends RoleToken<any>>(
  capabilityManager: CapabilityManager.CapabilityManager,
  args: { type: TToken; data?: TokenData<TToken> },
): boolean {
  const effectiveRole = args.type?.role;
  if (effectiveRole == null) {
    return false;
  }

  const surfaces = capabilityManager.getAll(Capabilities.ReactSurface);
  const candidates = findCandidates(surfaces.flat(), {
    role: effectiveRole,
    data: args.data as Props['data'],
  });
  return candidates.length > 0;
}
